import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TimeoutError } from 'rxjs';
import { TimeoutErrorHandler } from 'src/shared/providers/timeout-error-handler';
import { WindowService } from 'src/shared/providers/window.service';

@Component({
    selector: 'confirm-page',
    styleUrls: ['./confirm.component.scss'],
    templateUrl: './confirm.component.html',
    standalone: false
})

export class ConfirmPage implements OnInit {

  @Input() backText: string;
  @Input() body: string;
  @Input() bodyMarkdown: string;
  @Input() confirmText: string;
  @Input() icon = 'help_outline';
  @Input() title = '';
  @Input() confirmAction: (data?) => Promise<any>;
  @Input() confirmActionParams: any = null;
  @Input() customBackAction: (data?) => Promise<any>;
  @Input() blockAction: (data?) => Promise<any>;
  @Input() customBackParams: any = null;
  @Input() sameRowButtons = true;
  @Input() confirmButtonClass = 'primary';
  @Input() backButtonClass = 'secondary';
  @Input() showAutomaticBookingQuestion = false;
  @Input() reservationDateTime = ''
  errorMessage = '';
  isConfirming = false;
  isBackLoading = false;
  isBlocking = false;

  boundBackAction: () => Promise<any>;
  boundConfirm: () => Promise<any>;
  boundTriggerBlockAction: () => Promise<any>;

constructor(
    private modalCtrl: ModalController,
    private timeoutErrorHandler: TimeoutErrorHandler,
    public windowService: WindowService
  ) {
    this.boundBackAction = this.backAction.bind(this);
    this.boundConfirm = this.confirm.bind(this);
    this.boundTriggerBlockAction = this.triggerBlockAction.bind(this);
  }

  ngOnInit() {
    // Seed the stored value to match the checkbox's default-checked state so a
    // confirm without touching the checkbox keeps auto-schedule on (the visible
    // default) rather than acting on stale localStorage from a prior cancel.
    if (this.showAutomaticBookingQuestion) {
      localStorage.setItem('keepAutomaticBookingOn', 'true');
    }
  }

  backAction = async () => {
    if (this.customBackAction) {
      this.isBackLoading = true;
      try {
        await this.customBackAction(this.customBackParams);
      } catch (err) {
        this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      } finally {
        this.isBackLoading = false;
      }
    } else {
      this.modalCtrl.dismiss();
    }
  }

  async confirm() {
    this.isConfirming = true;
    try {
      await this.confirmAction(this.confirmActionParams);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    } finally {
      this.isConfirming = false;
    }
  }

  async triggerBlockAction() {
    this.isBlocking = true;
    try {
      await this.blockAction(this.confirmActionParams);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    } finally {
      this.isBlocking = false;
    }
  }

  timeoutHandler(error) {
    if (error instanceof TimeoutError) {
      this.title = this.timeoutErrorHandler.timeoutParams.header;
      this.body = this.timeoutErrorHandler.timeoutParams.body;
      this.confirmText = this.timeoutErrorHandler.timeoutParams.buttonText;
    }
    throw error;
  }

  updateAutomaticBooking(selection) {
    localStorage.setItem('keepAutomaticBookingOn', selection.toString());
  }

}

export interface ConfirmPageParamsModel {
  backButtonClass?: string;
  backText: string;
  blockAction?: (data?) => Promise<any>;
  body: string;
  bodyMarkdown?: string;
  confirmAction: (data?) => Promise<any>;
  confirmActionParams?: any;
  confirmButtonClass?: string;
  confirmText: string;
  customBackAction?: (data?) => Promise<any>;
  customBackParams?: any;
  icon?: string;
  reservationDateTime?: string;
  sameRowButtons?: boolean;
  showAutomaticBookingQuestion?: boolean;
  title: string;
}
